import { afterLoadWhenIdle } from "@/utils/after-load-idle";

// The classic Web Widget finds its key through this element id; keep it.
export const ZENDESK_SNIPPET_ID = 'ze-snippet';
export const ZENDESK_SNIPPET_URL = 'https://static.zdassets.com/ekr/snippet.js?key=f4e9e105-48fc-466f-a861-a57ed4b1a949';

/**
 * Below PrimeVue's modal layer (Toast, Drawer and Dialog stack from 1100) and its overlays (1000). Zendesk's default
 * sits above everything, so the launcher floated over the full-screen content slide-over on phones and an open widget
 * hid every toast. Toasts are top-right (bottom-centre on a phone, raised above the launcher's corner, index.css), and
 * the bottom-left overlays (the update prompt, and the install card that waits for it) keep their buttons clear of the
 * launcher's default bottom-right offset: the prompt's are centred on a phone and at its left from sm, and on a phone
 * the install card leaves the right 5rem free (PwaInstallCard.vue).
 * Only the stacking needed changing. It puts the launcher above both, so a bottom-left overlay that grows into that
 * corner is covered there.
 */
export const ZENDESK_Z_INDEX = 999;

/**
 * UX roadmap #27: the launcher keeps its default bottom-right place on every width (a product decision: it is not
 * hidden on phones), so the fixed and sticky layers that share that corner make room for it instead. On a phone the
 * widget shows an icon-only launcher, about 50px plus its margins, so the corner is taken as a 4.5rem square. The
 * property is set on <html> only once the snippet has loaded: where it is blocked (an ad blocker, a strict network),
 * nothing is reserved for a launcher that never appears. Read as `var(--support-launcher-inset, 0px)`: Private.vue's
 * <main> and the end of Public.vue's page (bottom padding below lg), the calculators' sticky summary bars (right padding
 * below sm), the RS Matrix's sticky tap readout (right padding on a phone either way up, RSMatrix.vue) and the phone
 * toast position (index.css).
 */
export const SUPPORT_LAUNCHER_INSET_VAR = '--support-launcher-inset';
export const SUPPORT_LAUNCHER_INSET = '4.5rem';

declare global {
    interface Window {
        zESettings?: Record< string, unknown >;
        /** The classic widget's API, defined by the snippet once it runs. */
        zE?: ( ...args: unknown[] ) => void;
    }
}

/**
 * UX roadmap #5: below lg the member tab bar (MobileTabBar.vue) holds the bottom of the screen, and the launcher, which
 * the widget places itself inside its own iframe, sat on it. The bar reports its height here, 0 while it is hidden or
 * absent, and the launcher is lifted by that much through the widget's offset setting: in zESettings when the snippet
 * loads, and through updateSettings once it has. `mobile` is the offset on a phone, the other the one on a tablet. At 0
 * nothing is sent, so a desktop, which never shows the bar, keeps the widget's own placement.
 */
let launcherLiftPx = 0;

function launcherOffset( lift: number ): Record< string, unknown > {
    const vertical = `${ lift }px`;
    return { horizontal: '0px', vertical, mobile: { horizontal: '0px', vertical } };
}

function widgetSettings(): Record< string, unknown > {
    return launcherLiftPx > 0
        ? { zIndex: ZENDESK_Z_INDEX, offset: launcherOffset( launcherLiftPx ) }
        : { zIndex: ZENDESK_Z_INDEX };
}

/** Lifts the support launcher `px` above the bottom of the screen (the tab bar's height); 0 puts it back. */
export function setSupportLauncherLift( px: number ): void {
    const lift = Math.max( 0, Math.round( px ) );
    if ( lift === launcherLiftPx ) return;
    launcherLiftPx = lift;

    // Injected but still loading: the snippet reads zESettings when it runs.
    if ( window.zESettings ) window.zESettings = { webWidget: widgetSettings() };
    try {
        window.zE?.( 'webWidget', 'updateSettings', { webWidget: { offset: launcherOffset( lift ) } } );
    } catch {
        // A third-party widget that failed to start has no launcher to move; the tab bar must not fail with it.
    }
}

/** Adds the snippet once; the snippet then loads the widget (~240 KB brotli, ~770 KB of JS to run). */
export function injectZendeskWidget(): void {
    if ( document.getElementById( ZENDESK_SNIPPET_ID ) ) return;

    window.zESettings = { webWidget: widgetSettings() };

    const script = document.createElement( 'script' );
    script.id = ZENDESK_SNIPPET_ID;
    script.async = true;
    script.src = ZENDESK_SNIPPET_URL;
    script.addEventListener( 'load', () => {
        document.documentElement.style.setProperty( SUPPORT_LAUNCHER_INSET_VAR, SUPPORT_LAUNCHER_INSET );
    }, { once: true } );
    document.body.appendChild( script );
}

/**
 * UX roadmap #26: the support launcher stays on every page, but its script no longer loads with the app. It was an
 * async <script> in index.html, so the widget downloaded and ran during startup on every visit, alongside the entry
 * chunk and the first API calls, although nothing in the app talks to it. It now loads after `load`, when idle.
 */
export function scheduleZendeskWidget(): () => void {
    return afterLoadWhenIdle( injectZendeskWidget );
}
